import {
	ChangeDetectorRef,
	Component,
	OnDestroy,
	ElementRef,
	ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { AuthService } from './../../service/auth.service';
import { NzModalService } from 'ng-zorro-antd/modal';

type SbPublicState =
	| 'authentication'
	| 'authorization'
	| 'authenticated'
	| 'completed';

@Component({
	selector: 'app-sb-public',
	standalone: false,
	templateUrl: './sb-public.component.html',
	styleUrl: './sb-public.component.scss',
})
export class SbPublicComponent implements OnDestroy {
	state: SbPublicState = 'authentication';

	completedCaseReference: string = '';
	completedCaseType: string = '';

	emailAddress: string = '';
	otpCode: string = '';
	countdown: number = 0;
	isSubmitting: boolean = false;

	private timerInterval: any;

	sbFormModel: any = {
		// DETAILS OF PERSON COMPLETING THE FORM
		contactTitle: '',
		contactFirstName: '',
		contactLastName: '',
		contactDesignation: '',
		contactEmail: '',
		contactNumber: '',

		// EMPLOYEE DETAILS
		employeeFirstName: '',
		employeeMiddleName: '',
		employeeLastName: '',
		employeeIdNumber: '',
		employeeDateOfBirth: '',
		employeeNumber: '',
		employeeEmail: '',
		employeeContactNumber: '',
		employeeDivision: '',

		// P&C BP DETAILS
		pcbpTitle: '',
		pcbpFirstName: '',
		pcbpLastName: '',
		pcbpDesignation: '',
		pcbpEmail: '',
		pcbpContactNumber: '',

		// LINE MANAGER DETAILS
		managerTitle: '',
		managerFirstName: '',
		managerLastName: '',
		managerDesignation: '',
		managerEmail: '',
		managerContactNumber: '',

		assessmentType: '',
		problemDescription: '',
		signature: '',
		dateOfSubmission: '',
	};

	documentUploadConfig: {
		[key: string]: { name: string; label: string; required: boolean }[];
	} = {
		Disability: [
			{ name: 'idCopy', label: 'ID Copy', required: true },
			{ name: 'selfReport', label: 'Self-Report', required: true },
			{ name: 'consentForm', label: 'Consent Form', required: true },
			{ name: 'eea1Form', label: 'EEA1 Form', required: true },
			{
				name: 'additionalMedicals',
				label: 'Additional Medicals',
				required: false,
			},
		],
		STIL: [
			{ name: 'consentForm', label: 'Consent Form', required: true },
			{ name: 'idCopy', label: 'ID Copy', required: true },
			{ name: 'referralForm', label: 'Referral Form', required: true },
			{
				name: 'doctorsNoteIcd10',
				label: "Doctor's Note with ICD-10 Code",
				required: true,
			},
			{
				name: 'additionalMedicals',
				label: 'Additional Medicals',
				required: false,
			},
		],
		Incapacity: [
			{ name: 'consentForm', label: 'Consent Form', required: true },
			{ name: 'idCopy', label: 'ID Copy', required: true },
			{ name: 'referralForm', label: 'Referral Form', required: true },
			{
				name: 'jobDescription',
				label: 'Job Description',
				required: true,
			},
			{
				name: 'performanceReport',
				label: 'Performance Report',
				required: true,
			},
			{
				name: 'additionalMedicals',
				label: 'Additional Medicals',
				required: false,
			},
		],
	};

	selectedAssessmentType: string = '';
	selectedFiles: { [key: string]: File[] } = {};
	formSubmitAttempted: boolean = false;

	getSelectedDocuments(): {
		name: string;
		label: string;
		required: boolean;
	}[] {
		if (!this.selectedAssessmentType) {
			return [];
		}

		return this.documentUploadConfig[this.selectedAssessmentType] || [];
	}

	onDocumentSelected(event: Event, documentName: string): void {
		const input = event.target as HTMLInputElement;

		if (!input.files || input.files.length === 0) {
			this.selectedFiles[documentName] = [];
			return;
		}

		this.selectedFiles[documentName] = Array.from(input.files);
	}

	constructor(
		private authService: AuthService,
		private modalService: NzModalService,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnDestroy(): void {
		this.clearTimer();
	}

	requestOtp(): void {
		if (!this.emailAddress) return;

		this.isSubmitting = true;

		this.authService.doSendSbOtp(this.emailAddress).subscribe({
			next: (result: any) => {
				this.isSubmitting = false;

				if (result.success === false) {
					this.modalService.error({
						nzTitle: 'Member Not Found',
						nzContent:
							result.message ||
							'No member exists for the email address provided.',
					});
					return;
				}

				this.otpCode = '';
				this.state = 'authorization';
				this.startCountdown();
			},
			error: () => {
				this.isSubmitting = false;
				this.modalService.error({
					nzTitle: 'Error',
					nzContent: 'Failed to send OTP.',
				});
			},
		});
	}

	verifyOtp(): void {
		if (!this.otpCode || this.otpCode.length < 4) return;

		this.isSubmitting = true;

		this.authService
			.doVerifySbOtp(this.emailAddress, this.otpCode)
			.subscribe({
				next: (result: any) => {
					this.isSubmitting = false;

					if (result.success === false) {
						this.modalService.error({
							nzTitle: 'Verification Failed',
							nzContent: result.message || 'Invalid OTP.',
						});
						return;
					}

					this.applyPrefillData(
						result.data || result.member || result.prefill,
					);
					this.state = 'authenticated';
				},
				error: () => {
					this.isSubmitting = false;
					this.modalService.error({
						nzTitle: 'Verification Failed',
						nzContent: 'Invalid OTP.',
					});
				},
			});
	}

	resendOtp(): void {
		if (this.countdown === 0) {
			this.requestOtp();
		}
	}

	goBackToEmail(): void {
		this.otpCode = '';
		this.state = 'authentication';
		this.clearTimer();
	}

	private startCountdown(): void {
		this.clearTimer();
		this.countdown = 30;
		this.cdr.markForCheck();

		this.timerInterval = setInterval(() => {
			if (this.countdown > 0) {
				this.countdown--;
				this.cdr.markForCheck();
			} else {
				this.clearTimer();
			}
		}, 1000);
	}

	private clearTimer(): void {
		if (this.timerInterval) {
			clearInterval(this.timerInterval);
			this.timerInterval = null;
		}
	}
	private getWorkflowLookupKey(type: string): string {
		if (type === 'STIL') return 'stil';
		if (type === 'Incapacity') return 'incapacity';
		if (type === 'Disability') return 'disability';

		throw new Error('Invalid assessment type');
	}

	hasRequiredDocumentsMissing(): boolean {
		const requiredDocs = this.getSelectedDocuments().filter(
			(doc) => doc.required,
		);
		return requiredDocs.some(
			(doc) =>
				!this.selectedFiles[doc.name] ||
				this.selectedFiles[doc.name].length === 0,
		);
	}

	submitForm(form: NgForm): void {
		this.formSubmitAttempted = true;

		if (
			form.invalid ||
			!this.signatureData ||
			this.hasRequiredDocumentsMissing()
		) {
			return;
		}

		const formValue = form.value;

		const workflowLookupKey = this.getWorkflowLookupKey(
			formValue.assessmentType,
		);

		const payload = {
			workflowLookupKey,
			submittedByEmail: this.emailAddress,
			formData: formValue,
		};

		const formData = new FormData();

		formData.append(
			'payload',
			new Blob([JSON.stringify(payload)], {
				type: 'application/json',
			}),
		);

		Object.keys(this.selectedFiles).forEach((documentName) => {
			this.selectedFiles[documentName].forEach((file) => {
				formData.append(documentName, file, file.name);
			});
		});

		const signatureFile = this.dataUrlToFile(
			this.signatureData,
			'sb-form-signature.png',
		);

		if (signatureFile) {
			formData.append(
				'signatureDocument',
				signatureFile,
				signatureFile.name,
			);
		}

		console.log('SB capture multipart payload:', payload);
		console.log('SB capture selected files:', this.selectedFiles);

		this.isSubmitting = true;

		this.authService.doSubmitSbCapture(formData).subscribe({
			next: (result: any) => {
				this.isSubmitting = false;

				if (result.success === false) {
					this.modalService.error({
						nzTitle: 'Submission Failed',
						nzContent:
							result.message ||
							'The submission could not be completed.',
					});
					return;
				}

				const responseData = result.data || {};

				this.completedCaseReference =
					responseData.ticketReference ||
					responseData.caseReference ||
					'';

				this.completedCaseType = this.getCaseTypeLabel(
					responseData.caseType || this.selectedAssessmentType,
				);

				this.state = 'completed';
			},
			error: () => {
				this.isSubmitting = false;
				this.modalService.error({
					nzTitle: 'Submission Failed',
					nzContent: 'The submission could not be completed.',
				});
			},
		});
	}

	private signatureCanvasRef?: ElementRef<HTMLCanvasElement>;

	@ViewChild('signatureCanvas')
	set signatureCanvas(canvas: ElementRef<HTMLCanvasElement> | undefined) {
		if (!canvas) {
			return;
		}

		this.signatureCanvasRef = canvas;

		setTimeout(() => {
			this.setupSignatureCanvas();
		});
	}

	signatureData: string = '';

	private ctx?: CanvasRenderingContext2D;
	private isDrawing = false;

	// SETUP THE SIGNATURE CANVAS
	private setupSignatureCanvas(): void {
		if (!this.signatureCanvasRef) {
			return;
		}

		const canvas = this.signatureCanvasRef.nativeElement;
		const rect = canvas.getBoundingClientRect();

		canvas.width = rect.width || 600;
		canvas.height = 180;

		const context = canvas.getContext('2d');

		if (!context) {
			return;
		}

		this.ctx = context;
		this.ctx.lineWidth = 2;
		this.ctx.lineCap = 'round';
		this.ctx.lineJoin = 'round';
		this.ctx.strokeStyle = '#000';
	}

	startDrawing(event: MouseEvent | TouchEvent): void {
		event.preventDefault();

		if (!this.ctx) {
			this.setupSignatureCanvas();
		}

		if (!this.ctx) {
			return;
		}

		const position = this.getCanvasPosition(event);

		this.isDrawing = true;
		this.ctx.beginPath();
		this.ctx.moveTo(position.x, position.y);
	}

	draw(event: MouseEvent | TouchEvent): void {
		if (!this.isDrawing || !this.ctx) {
			return;
		}

		event.preventDefault();

		const position = this.getCanvasPosition(event);

		this.ctx.lineTo(position.x, position.y);
		this.ctx.stroke();

		this.updateSignatureData();
	}

	stopDrawing(): void {
		if (!this.isDrawing) {
			return;
		}

		this.isDrawing = false;
		this.updateSignatureData();
	}

	clearSignature(): void {
		if (!this.signatureCanvasRef || !this.ctx) {
			return;
		}

		const canvas = this.signatureCanvasRef.nativeElement;

		this.ctx.clearRect(0, 0, canvas.width, canvas.height);
		this.signatureData = '';
	}

	private updateSignatureData(): void {
		if (!this.signatureCanvasRef) {
			return;
		}

		const canvas = this.signatureCanvasRef.nativeElement;
		this.signatureData = canvas.toDataURL('image/png');
	}

	private getCanvasPosition(event: MouseEvent | TouchEvent): {
		x: number;
		y: number;
	} {
		if (!this.signatureCanvasRef) {
			return { x: 0, y: 0 };
		}

		const canvas = this.signatureCanvasRef.nativeElement;
		const rect = canvas.getBoundingClientRect();

		let clientX: number;
		let clientY: number;

		if (event instanceof TouchEvent) {
			clientX =
				event.touches[0]?.clientX || event.changedTouches[0].clientX;
			clientY =
				event.touches[0]?.clientY || event.changedTouches[0].clientY;
		} else {
			clientX = event.clientX;
			clientY = event.clientY;
		}

		return {
			x: clientX - rect.left,
			y: clientY - rect.top,
		};
	}
	private dataUrlToFile(dataUrl: string, fileName: string): File | null {
		if (!dataUrl) {
			return null;
		}

		const parts = dataUrl.split(',');
		const metadata = parts[0];
		const base64Data = parts[1];

		if (!metadata || !base64Data) {
			return null;
		}

		const mimeMatch = metadata.match(/:(.*?);/);
		const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';

		const binaryString = atob(base64Data);
		const bytes = new Uint8Array(binaryString.length);

		for (let i = 0; i < binaryString.length; i++) {
			bytes[i] = binaryString.charCodeAt(i);
		}

		return new File([bytes], fileName, { type: mimeType });
	}

	private applyPrefillData(prefill: any): void {
		if (!prefill) {
			return;
		}

		// PERSON COMPLETING THE FORM, AUTHENTICATED INDIVIDUAL
		this.sbFormModel.contactTitle = prefill.title || '';
		this.sbFormModel.contactFirstName = prefill.name || '';
		this.sbFormModel.contactLastName = prefill.surname || '';
		this.sbFormModel.contactEmail =
			prefill.emailAddress || this.emailAddress;
		this.sbFormModel.contactNumber = prefill.msisdn || '';
		this.sbFormModel.contactDesignation =
			prefill.jobTitle || prefill.designation || '';

		// EMPLOYEE DETAILS, FOR NOW DEFAULT TO THE SAME AUTHENTICATED MEMBER.

		this.sbFormModel.employeeFirstName = prefill.name || '';
		this.sbFormModel.employeeLastName = prefill.surname || '';
		this.sbFormModel.employeeIdNumber = prefill.idNumber || '';
		this.sbFormModel.employeeDateOfBirth = prefill.dateOfBirth || '';
		this.sbFormModel.employeeNumber = prefill.payrollNumber || '';
		this.sbFormModel.employeeEmail = prefill.emailAddress || '';
		this.sbFormModel.employeeContactNumber = prefill.msisdn || '';
		this.sbFormModel.employeeDivision = prefill.division || '';

		// P&C BP DETAILS, FROM DYNAMIC PROPERTIES
		this.sbFormModel.pcbpTitle = prefill.pcbpTitle || '';
		this.sbFormModel.pcbpFirstName = prefill.pcbpFirstName || '';
		this.sbFormModel.pcbpLastName = prefill.pcbpLastName || '';
		this.sbFormModel.pcbpDesignation = prefill.pcbpDesignation || '';
		this.sbFormModel.pcbpEmail = prefill.pcbpEmail || '';
		this.sbFormModel.pcbpContactNumber = prefill.pcbpContactNumber || '';

		// LINE MANAGER DETAILS, FROM DYNAMIC PROPERTIES
		this.sbFormModel.managerTitle = prefill.managerTitle || '';
		this.sbFormModel.managerFirstName = prefill.managerFirstName || '';
		this.sbFormModel.managerLastName = prefill.managerLastName || '';
		this.sbFormModel.managerDesignation = prefill.managerDesignation || '';
		this.sbFormModel.managerEmail = prefill.managerEmail || '';
		this.sbFormModel.managerContactNumber =
			prefill.managerContactNumber || '';

		this.sbFormModel.dateOfSubmission = new Date()
			.toISOString()
			.substring(0, 10);
	}

	private getCaseTypeLabel(type: string): string {
		if (type === 'STIL') {
			return 'Short Term Incapacity Leave';
		}

		if (type === 'Incapacity') {
			return 'Incapacity / Fitness for Work';
		}

		if (type === 'Disability') {
			return 'Disability Verification';
		}

		return type || '';
	}
}
